import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, FormGroup, UntypedFormControl, Validators, ValidatorFn, UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs/Rx';
import { IdentityService } from 'identity';
import { DateService, MessageService } from 'base';
import { TenantDto } from 'UvExplorerServer.Dtos.TS/Utils/TenantDto';
import { SuperServerDto } from 'UvExplorerServer.Dtos.TS/LinkedServers/SuperServerDto';
import { SubServerDto } from 'UvExplorerServer.Dtos.TS/LinkedServers/SubServerDto';
import { ComponentBase } from 'common';
import { ConfigurationService } from 'configuration';
import { GetLinkedServersResultDto } from 'UvExplorerServer.Dtos.TS/Session/GetLinkedServersResultDto';

@Component({
	selector: 'linked-servers',
	templateUrl: './linked-servers.component.html',
	styleUrls: ['./linked-servers.component.scss']
})
export class LinkedServersComponent extends ComponentBase implements OnInit, OnDestroy {

	public tenant: TenantDto;
	public superServer: SuperServerDto;
	public subServers: SubServerDto[];

	public form: UntypedFormGroup;

	public name: UntypedFormControl;
	public serverId: UntypedFormControl;
	public tenantId: UntypedFormControl;
	public serverUrl: UntypedFormControl;
	public serverApiKey: UntypedFormControl;
	public syncInterval: UntypedFormControl;
	public lastSyncStartTime: UntypedFormControl;
	public lastSyncEndTime: UntypedFormControl;
	public syncEnabled: UntypedFormControl;
	public awolEnabled: UntypedFormControl;
	public awolDays: UntypedFormControl;
	public awolHours: UntypedFormControl;
	public awolMinutes: UntypedFormControl;

	public displayUpdateSuperServerConfirm: boolean;
	public displayUnlinkSuperServerConfirm: boolean;
	public displayUnlinkSubServerConfirm: boolean;

	constructor(private fb: UntypedFormBuilder,
		private configService: ConfigurationService,
		private identityService: IdentityService,
		private dateService: DateService,
		private messageService: MessageService) {

		super();

		this.tenant = null;
		this.superServer = null;
		this.subServers = [];

		this.displayUpdateSuperServerConfirm = false;
		this.displayUnlinkSuperServerConfirm = false;
		this.displayUnlinkSubServerConfirm = false;
	
		this.buildForm();
	}

	public get hasTenant(): boolean {
		return !!this.tenant;
	}

	public get hasSuperServer(): boolean {
		return !!this.superServer;
	}

	public get hasSubServers(): boolean {
		return (!!this.subServers && this.subServers.length > 0);
	}

	public get isSuperServerAwol(): boolean {
		return (!!this.superServer && this.superServer.Awol);
	}

	public formatDateHm(date: string | Date): string {
		return this.dateService.formatDateHm(date);
	}

	public ngOnDestroy(): void {

	}

	public ngOnInit() {	
		this.getLinkedServers();
	}

	private getLinkedServers(): void {
		this.configService.getLinkedServers().subscribe(
			(result: GetLinkedServersResultDto) => {
				this.tenant = result.Tenant;
				this.superServer = result.SuperServer;
				this.subServers = result.SubServers.ToArray();

				this.buildForm();
			},
			(error) => {
			this.messageService.displayErrorMessage("Could not retrieve linked servers");
			});
	}

	private buildForm(): void {
		let name = "";
		let serverId = "";
		let tenantId = "";
		let serverUrl = "";
		let serverApiKey = "";
		let syncInterval = 0;
		let lastSyncStartTime = "";
		let lastSyncEndTime = "";
		let syncEnabled = false;
		let awolEnabled = false;
		let awolDays = 0;
		let awolHours = 0;
		let awolMinutes = 0;

		if (this.hasTenant) {
			serverId = this.tenant.ServerGuid;
			tenantId = this.tenant.TenantGuid;
		}
		if (this.hasSuperServer) {
			name = this.superServer.Name;
			serverUrl = this.superServer.ServerUrl;
			serverApiKey = this.superServer.ServerApiKey;
			syncInterval = this.superServer.SyncInterval;
			lastSyncStartTime = this.superServer.LastSyncStartTime;
			lastSyncEndTime = this.superServer.LastSyncEndTime;
			syncEnabled = this.superServer.SyncEnabled;
			awolEnabled = this.superServer.AwolEnabled;
			awolDays = this.superServer.AwolDays;
			awolHours = this.superServer.AwolHours;
			awolMinutes = this.superServer.AwolMinutes;
		}

		this.serverId = new UntypedFormControl(serverId);
		this.tenantId = new UntypedFormControl(tenantId);
		this.name = new UntypedFormControl(name);
		this.serverUrl = new UntypedFormControl(serverUrl);
		this.serverApiKey = new UntypedFormControl(serverApiKey);
		this.syncInterval = new UntypedFormControl(syncInterval);
		this.lastSyncStartTime = new UntypedFormControl(lastSyncStartTime);
		this.lastSyncEndTime = new UntypedFormControl(lastSyncEndTime);
		this.syncEnabled = new UntypedFormControl(syncEnabled);
		this.awolEnabled = new UntypedFormControl(awolEnabled);
		this.awolDays = new UntypedFormControl(awolDays);
		this.awolHours = new UntypedFormControl(awolHours);
		this.awolMinutes = new UntypedFormControl(awolMinutes);

		this.enableDisableControls();

		this.form = this.fb.group({
			serverId: this.serverId,
			tenantId: this.tenantId,
			name: this.name,
			serverUrl: this.serverUrl,
			serverApiKey: this.serverApiKey,
			syncInterval: this.syncInterval,
			lastSyncStartTime: this.lastSyncStartTime,
			lastSyncEndTime: this.lastSyncEndTime,
			syncEnabled: this.syncEnabled,
			awolEnabled: this.awolEnabled,
			awolDays: this.awolDays,
			awolHours: this.awolHours,
			awolMinutes: this.awolMinutes,
		},
		{
			validator: this.validateForm(),
		});
	}

	private validateForm(): ValidatorFn {
		return () => {
			let errors: string[] = this.validateFormValues();
			
			if (errors.length > 0) {
				let result = {}

				errors.forEach((errorName: string) => {
					result[errorName] = { value: false };					
				})

				return result;
			}
			else {
				return null;
			};
		}
	}

	private validateFormValues(): string[] {
		let errors: string[] = [];
		const MinutesPerDay = 1440;
		
		if (!!this.superServer || !this.emptySuperServerSettings) {
			if (this.isNullOrWhitespace(this.name.value)) {
				errors.push("invalidName");
			}
			if (this.isNullOrWhitespace(this.serverUrl.value)) {
				errors.push("invalidServerUrl");
			}
			if (this.isNullOrWhitespace(this.serverApiKey.value)) {
				errors.push("invalidServerApiKey");
			}
			if (this.syncEnabled.value) {
				if (this.syncInterval.value <= 0 || this.syncInterval.value > MinutesPerDay) {
					errors.push("invalidSyncInterval");
				}
			}
			if (this.syncEnabled.value && this.awolEnabled.value) {
				if (this.awolDays.value == 0 && this.awolHours.value == 0 && this.awolMinutes.value == 0) {
					errors.push("invalidAwolInterval");
				}
			}
			if (this.syncEnabled.value && this.awolEnabled.value) {
				if (this.awolDays.value < 0 || this.awolDays.value > 30 ||
					this.awolHours.value < 0 || this.awolHours.value > 23 ||
					this.awolMinutes.value < 0 || this.awolMinutes.value > 59) {

					errors.push("invalidAwolInterval");
				}
			}
		}

		return errors;
	}

	private isNullOrWhitespace(s: string): boolean {
		return !s.trim();
	}

	private setFormError(result: object, errorName: string) {
		result[errorName] = { value: false };
	}

	private get emptySuperServerSettings(): boolean {
		return (!this.name.value &&
				!this.serverUrl.value &&
				!this.serverApiKey.value &&
				!this.syncInterval.value &&
				!this.syncEnabled.value);
	}

	private get isSuperServer(): boolean {
		return (!!this.subServers && this.subServers.length > 0);
	}

	private get isSubServer(): boolean {
		return !!this.superServer;
	}

	public enableDisableControls(): void {
		this.enableDisableFormControl(this.serverUrl, this.enableServerUrl);
		this.enableDisableFormControl(this.serverApiKey, this.enableServerApiKey);
		this.enableDisableFormControl(this.syncEnabled, this.enableSyncEnabled);
		this.enableDisableFormControl(this.syncInterval, this.enableSyncInterval);
		this.enableDisableFormControl(this.awolEnabled, this.enableAwolEnabled);
		this.enableDisableFormControl(this.awolDays, this.enableAwolInputs);
		this.enableDisableFormControl(this.awolHours, this.enableAwolInputs);
		this.enableDisableFormControl(this.awolMinutes, this.enableAwolInputs);
		this.enableDisableFormControl(this.name, this.enableName);
	}

	public get enableServerUrl(): boolean {
		return !this.isSuperServer;
	}

	public get enableServerApiKey(): boolean {
		return !this.isSuperServer;
	}

	public get enableSyncEnabled(): boolean {
		return !this.isSuperServer;
	}

	public get enableSyncInterval(): boolean {
		return (!this.isSuperServer && this.syncEnabled.value);
	}

	public get enableAwolEnabled(): boolean {
		return (!this.isSuperServer && this.syncEnabled.value);
	}

	public get enableAwolInputs(): boolean {
		return (!this.isSuperServer && this.syncEnabled.value && this.awolEnabled.value);
	}

	public get enableName(): boolean {
		return !this.isSuperServer;
	}

	public get enableUpdateSuperServerButton(): boolean {
		let errors: string[] = this.validateFormValues();
		return (!this.isSuperServer && this.form.dirty && !this.emptySuperServerSettings && errors.length == 0);
	}

	public get enableUnlinkSuperServerButton(): boolean {
		return this.isSubServer;
	}

	public get enableSyncNowButton(): boolean {
		return this.isSubServer;
	}

	public tryUpdateSuperServer(): void {
		this.displayUpdateSuperServerConfirm = true;
	}

	public cancelUpdateSuperServer(): void {
		this.displayUpdateSuperServerConfirm = false;
	}

	public updateSuperServer(): void {
		this.displayUpdateSuperServerConfirm = false;

		let superServer = new SuperServerDto();
		superServer.ServerGuid = this.identityService.session.ServerGuid;
		superServer.TenantGuid = this.identityService.session.TenantGuid;
		superServer.Name = this.name.value;
		superServer.ServerUrl = this.serverUrl.value;
		superServer.ServerApiKey = this.serverApiKey.value;
		superServer.SyncInterval = this.syncInterval.value;
		superServer.LastSyncStartTime = this.lastSyncStartTime.value;
		superServer.LastSyncEndTime = this.lastSyncEndTime.value;
		superServer.SyncEnabled = this.syncEnabled.value;
		superServer.AwolEnabled = this.awolEnabled.value;
		superServer.AwolDays = this.awolDays.value;
		superServer.AwolHours = this.awolHours.value;
		superServer.AwolMinutes = this.awolMinutes.value;

		this.messageService.startWork();
		this.configService.linkSuperServer(superServer).subscribe(
			(result: boolean) => {
				this.messageService.endWork();
				this.messageService.displayInfoMessage("Parent server link has been updated");
				this.getLinkedServers();
			},
			(error) => {
				this.messageService.endWork();
				this.messageService.displayErrorMessage("Could not update link to parent server");
				this.getLinkedServers();
			});
	}

	public tryUnlinkSuperServer(): void {
		this.displayUnlinkSuperServerConfirm = true;
	}

	public cancelUnlinkSuperServer(): void {
		this.displayUnlinkSuperServerConfirm = false;
	}

	public unlinkSuperServer(): void {
		this.displayUnlinkSuperServerConfirm = false;

		this.messageService.startWork();
		this.configService.unlinkSuperServer().subscribe(
			(result: boolean) => {
				this.messageService.endWork();
				this.messageService.displayInfoMessage("The parent server has been unlinked");
				this.getLinkedServers();
			},
			(error) => {
				this.messageService.endWork();
				this.messageService.displayErrorMessage("Could not unlink from parent server");
				this.getLinkedServers();
			});
	}

	public syncNow(): void {
		this.messageService.startWork();
		this.configService.syncWithSuperServer().subscribe(
			(result: boolean) => {
				this.messageService.endWork();
				this.messageService.displayInfoMessage("Sync has been initiated");
				this.getLinkedServers();
			},
			(error) => {
				this.messageService.endWork();
				this.messageService.displayErrorMessage("Sync could not be initiated");
				this.getLinkedServers();
			});
	}

	private subServerToUnlink: SubServerDto;

	public tryUnlinkSubServer(event: any, subServer: SubServerDto): void {
		event.stopPropagation();
		this.subServerToUnlink = subServer;
		this.displayUnlinkSubServerConfirm = true;
	}

	public cancelUnlinkSubServer(): void {
		this.subServerToUnlink = null;
		this.displayUnlinkSubServerConfirm = false;
	}

	public unlinkSubServer(): void {
		let subServer: SubServerDto = this.subServerToUnlink;

		this.subServerToUnlink = null;
		this.displayUnlinkSubServerConfirm = false;

		this.messageService.startWork();
		this.configService.unlinkSubServer(subServer).subscribe(
			(result: boolean) => {
				this.messageService.endWork();
				this.getLinkedServers();
			},
			(error) => {
				this.messageService.endWork();
				this.messageService.displayErrorMessage("Sync could not be initiated");
				this.getLinkedServers();
			});
	}

	public syncAlertsString(subServer: SubServerDto): string {
		if (subServer.AwolEnabled) {
			let result = "After ";

			if (subServer.AwolDays == 1) {
				result += `${subServer.AwolDays} day`
			}
			else if (subServer.AwolDays > 1) {
				result += `${subServer.AwolDays} days`
			}
			
			if (subServer.AwolHours == 1) {
				result += `${subServer.AwolHours} hour`
			}
			else if (subServer.AwolHours > 1) {
				result += `${subServer.AwolHours} hours`
			}
			
			if (subServer.AwolMinutes == 1) {
				result += `${subServer.AwolMinutes} minute`
			}
			else if (subServer.AwolMinutes > 1) {
				result += `${subServer.AwolMinutes} minutes`
			}

			return result;
		}
		else {
			return "Disabled";
		}
	}

}
